import React, { useEffect, useRef } from 'react';
import { Edit, Trash2, Copy, RotateCcw, GitFork, Download, Eye } from 'lucide-react';

const VersionContextMenu = ({ 
  position, 
  version, 
  onClose, 
  onAmend, 
  onDelete, 
  onCopyContent, 
  onDuplicate, 
  onSetActive, 
  onExport,
  onViewDetails,
  isActive 
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const menuItems = [
    {
      id: 'amend',
      label: 'Amend Version',
      icon: Edit,
      onClick: () => {
        onAmend(version);
        onClose();
      },
      description: 'Edit this version without creating a new one'
    },
    {
      id: 'copy',
      label: 'Copy Content',
      icon: Copy,
      onClick: () => {
        onCopyContent(version);
        onClose();
      },
      description: 'Copy the version content to clipboard'
    },
    {
      id: 'duplicate',
      label: 'Duplicate Version',
      icon: GitFork,
      onClick: () => {
        onDuplicate(version);
        onClose();
      },
      description: 'Create a new version based on this one'
    },
    ...(isActive ? [] : [{
      id: 'activate',
      label: 'Set as Active',
      icon: RotateCcw,
      onClick: () => {
        onSetActive(version);
        onClose();
      },
      description: 'Make this the active version'
    }]),
    {
      id: 'details',
      label: 'View Details',
      icon: Eye,
      onClick: () => {
        onViewDetails(version);
        onClose();
      },
      description: 'View detailed information about this version'
    },
    {
      id: 'export',
      label: 'Export Version',
      icon: Download,
      onClick: () => {
        onExport(version);
        onClose();
      },
      description: 'Export this version as a file'
    },
    {
      id: 'divider',
      type: 'divider'
    },
    {
      id: 'delete',
      label: 'Delete Version',
      icon: Trash2,
      onClick: () => {
        onDelete(version);
        onClose();
      },
      className: 'text-red-600 hover:bg-red-50',
      description: 'Permanently delete this version',
      dangerous: true
    }
  ];

  if (!position) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div
        ref={menuRef}
        className="absolute bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-48 max-w-64"
        style={{
          left: position.x,
          top: position.y,
          transform: position.x > window.innerWidth - 200 ? 'translateX(-100%)' : 'none'
        }}
      >
        {menuItems.map((item) => {
          if (item.type === 'divider') {
            return (
              <div key={item.id} className="border-t border-gray-200 my-1" />
            );
          }

          const Icon = item.icon;
          
          return (
            <button
              key={item.id}
              onClick={item.onClick}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center transition-colors ${
                item.className || 'text-gray-700'
              }`}
              title={item.description}
            >
              <Icon className="h-4 w-4 mr-3" />
              <div className="flex-1">
                <div className="font-medium">{item.label}</div>
                {item.description && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    {item.description}
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default VersionContextMenu; 